import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLinkSlash } from '@rq-ratings/pro-solid-svg-icons'
import { useMutation } from '@tanstack/react-query'
import React from 'react'

import ActionButton from '../../../../components/misc/ActionButton'
import useNotyf from '../../../../hooks/useNotyf'
import companyUserIntegrationService from '../../../../lib/services/companyUserIntegrationService'
import { INTEGRATION_SERVICE, IntegrationService } from '../../../../types/misc'

interface Props {
  companyUserIntegrationId: number
  integratedService: IntegrationService
  onMutationStarted: () => void
  onDeleted: () => void
  onError: () => void
  disabled: boolean
  isCompanySchedulerOwner?: boolean
}

const DeleteIntegrationButton: React.FC<Props> = ({
  companyUserIntegrationId,
  integratedService,
  onMutationStarted,
  onDeleted,
  onError,
  disabled,
  isCompanySchedulerOwner = false,
}) => {
  const notyf = useNotyf()

  const deleteMutation = useMutation({
    mutationFn: () =>
      companyUserIntegrationService.deleteById(companyUserIntegrationId),
    onMutate: onMutationStarted,
    onSuccess: onDeleted,
    onError: () => {
      notyf.error('Disconnect failed. Please try again.')
      onError()
    },
  })

  const buttonLabel =
    integratedService === INTEGRATION_SERVICE.nylasCalendar
      ? 'Disconnect calendar'
      : 'Disconnect email'

  function createConfirmMessage() {
    if (integratedService === INTEGRATION_SERVICE.nylasCalendar) {
      const baseCalendarConfirmMessage =
        'Disconnect your calendar account? Your scheduler will be disabled, and all of your scheduler configuration will be lost.'

      if (isCompanySchedulerOwner) {
        return `${baseCalendarConfirmMessage} Additionally, since you are the owner of your company's scheduler, disconnecting your calendar account will delete your company's scheduler configuration and disable that scheduler.`
      }

      return baseCalendarConfirmMessage
    }

    return "Disconnect your email account? Referral and other emails will revert to being sent from your company's default email address on RQ."
  }

  return (
    <ActionButton
      isProcessing={deleteMutation.isPending}
      isProcessingText="Disconnecting"
      variant="outline-danger"
      size="sm"
      onClick={() => {
        if (window.confirm(createConfirmMessage())) {
          deleteMutation.mutate()
        }
      }}
      disabled={disabled}
    >
      <FontAwesomeIcon icon={faLinkSlash} className="me-2" />
      {buttonLabel}
    </ActionButton>
  )
}

export default DeleteIntegrationButton
